import { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AnimatedShape from './AnimatedShape';

export default function HeroSection() {
  const { t } = useTranslation('common');

  return (
    <section className="relative w-full h-screen overflow-hidden">
      {/* 3D Background */}
      <div className="absolute inset-0 z-0">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Suspense fallback={null}>
            <ambientLight intensity={0.5} />
            <directionalLight position={[10, 10, 5]} intensity={1} />
            <pointLight position={[-10, -10, -5]} intensity={0.5} />
            <AnimatedShape />
            <OrbitControls enableZoom={false} enablePan={false} autoRotate autoRotateSpeed={0.5} />
          </Suspense>
        </Canvas>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
      </div>

      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="relative z-30 h-full flex flex-col items-center justify-center px-6 md:px-12"
      >
        <div className="text-center max-w-5xl">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            <div className="text-6xl md:text-8xl lg:text-9xl font-thin leading-tight">
              {t('hero.title1')}
            </div>
            <div className="text-5xl md:text-7xl lg:text-8xl font-thin italic mt-2 leading-tight">
              {t('hero.title2')}
            </div>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-white/90 text-base md:text-lg max-w-3xl mx-auto leading-relaxed mt-8"
            style={{ lineHeight: '1.8' }}
          >
            {t('hero.description')}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12"
          >
            <a
              href="#projects"
              className="bg-black text-white px-12 py-4 rounded-full text-sm font-light tracking-wide hover:bg-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('hero.exploreProjects')}
            </a>
            <a
              href="#ar-technology"
              className="bg-transparent border border-white text-white px-12 py-4 rounded-full text-sm font-light tracking-wide hover:bg-white hover:text-black transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-smartphone-line text-lg"></i>
              {t('hero.viewAR')}
            </a>
          </motion.div>
        </div>

        {/* Scrolling Info Band */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="absolute bottom-8 left-0 right-0 overflow-hidden"
        >
          <div className="flex whitespace-nowrap animate-scroll">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center text-white text-sm font-light tracking-wider">
                <span className="mx-8">{t('hero.europeanStandards')}</span>
                <span className="mx-2">•</span>
                <span className="mx-8">{t('hero.globalDelivery')}</span>
                <span className="mx-2">•</span>
                <span className="mx-8">{t('hero.arTechnology')}</span>
                <span className="mx-2">•</span>
                <span className="mx-8">{t('hero.customDesign')}</span>
                <span className="mx-2">•</span>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
